// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: connection status for the accounts Konvo links to (Microsoft 365 and
// ClickUp). Shared by the settings tab and the page empty states so they never
// disagree about what is connected.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {applyProactivePatch} from '@app/features/konvo_connections/state/KonvoProactive';
import type {KonvoAssistantUsage} from '@app/features/konvo_connections/state/KonvoUsage';
import {connectionSignInBody, openConnectionSignIn} from '@app/features/konvo_connections/utils/ConnectionSignIn';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import type {AssistantResponseBlock} from '@konvo/schema/src/domains/konvo/AssistantResponse';
import {makeAutoObservable, runInAction} from 'mobx';

export interface KonvoClickUpStatus {
	oauth_available: boolean;
	connected: boolean;
	clickup_email: string | null;
}

export type KonvoClaudeModel = 'sonnet';

export interface KonvoClaudeStatus {
	available: boolean;
	connected: boolean;
	enabled: boolean;
	model: KonvoClaudeModel;
	status: 'ok' | 'auth_error' | 'error' | null;
	last_error: string | null;
	memory: string;
	turns: number;
	connected_at: string | null;
	last_used_at: string | null;
	session_started_at: string | null;
	onboarding_dismissed?: boolean;
}

export type KonvoAssistantProvider = 'claude' | 'codex';
export type KonvoAssistantAutonomy = 'suggest' | 'prepare' | 'approve' | 'routine';
export interface KonvoCodexStatus {
	available: boolean;
	connected: boolean;
	enabled: boolean;
	email: string | null;
	memory: string;
	status: 'ok' | 'auth_error' | 'error' | null;
	last_error: string | null;
	turns: number;
	connected_at: string | null;
	last_used_at: string | null;
}
/** What can wake the proactive engine up; `chat_message` feeds the composer strip. */
export type KonvoProactiveTriggerKind =
	| 'meeting_soon'
	| 'morning'
	| 'vip_mail'
	| 'task_overdue'
	| 'commitment_due'
	| 'chat_message'
	| 'waiting'
	| 'wrap_up';
export interface KonvoProactiveSettings {
	/** Master switch: when off, Konvo only speaks when spoken to. */
	enabled: boolean;
	triggers: Record<KonvoProactiveTriggerKind, boolean>;
	/** Quiet hours are everything outside active_from..active_until ('HH:MM', the user's timezone). */
	active_from: string;
	active_until: string;
	weekends: boolean;
	/** Things surfaced per day. */
	max_items_per_day: number;
	/** Assistant calls the engine may make per day to decide whether something is worth surfacing. */
	max_decisions_per_day: number;
	/** Extra VIP addresses or @domains on top of the ones Konvo infers. */
	vips: Array<string>;
	/** The end-of-day wrap-up comes this many minutes before active_until (servers before it leave it out). */
	wrap_up_minutes?: number;
	/** The wrap-up may offer to start coding agents on the person's own machines. */
	wrap_up_agents?: boolean;
	/**
	 * Konvo looks at received chat messages as they arrive (chips ready when the chat opens).
	 * Written from this device's "Suggestions in chats" switch; absent on older servers.
	 */
	chat_arrival?: boolean;
}
export type KonvoProactiveSettingsPatch = Partial<Omit<KonvoProactiveSettings, 'triggers'>> & {
	triggers?: Partial<Record<KonvoProactiveTriggerKind, boolean>>;
};
export interface KonvoProactiveTrigger {
	kind: KonvoProactiveTriggerKind;
	label: string;
	description: string;
	surface: 'dm' | 'strip';
	enabled: boolean;
}
export type KonvoProactiveOutcome = 'used' | 'dismissed';
/** How picky the engine has become about one trigger, from what the user did with the last ones. */
export type KonvoProactiveTuning = 'normal' | 'selective' | 'strict';
/** What happened to one trigger's surfaced items over the last 14 days. */
export interface KonvoProactiveTriggerStats {
	used: number;
	dismissed: number;
	/** Marked unwanted. */
	bad: number;
	/** Seen but neither used nor dismissed. */
	maybe: number;
	/** Too recent to tell yet. */
	pending: number;
	tuning: KonvoProactiveTuning;
}
export interface KonvoProactiveEngine {
	settings: KonvoProactiveSettings;
	triggers: Array<KonvoProactiveTrigger>;
	today: {items: number; decisions: number; max_items: number; max_decisions: number};
	/** How the Assistant was used over the last 14 days (engine/UsageLog on the server). */
	usage?: KonvoAssistantUsage;
	recent: Array<{
		item_id: string;
		kind: KonvoProactiveTriggerKind;
		surface: 'brief' | 'nudge' | 'strip' | 'none';
		title: string;
		created_at: string;
		outcome: KonvoProactiveOutcome | 'bad' | null;
		blocks?: Array<AssistantResponseBlock>;
	}>;
	/** Absent on servers from before the engine kept score. */
	stats?: Partial<Record<KonvoProactiveTriggerKind, KonvoProactiveTriggerStats>>;
}
export interface KonvoAssistantSettings {
	/** Post replies as they are written instead of all at once. */
	streaming: boolean;
	/** Zone the user chose or their device reported; null until one is known. */
	timezone: string | null;
	/** What the assistant actually uses. */
	effective_timezone: string;
	/** Absent until the server has the proactive engine. */
	proactive?: KonvoProactiveSettings;
}
export interface KonvoAssistantStatus {
	provider: KonvoAssistantProvider;
	autonomy: KonvoAssistantAutonomy;
	settings?: KonvoAssistantSettings;
	claude: KonvoClaudeStatus;
	codex: KonvoCodexStatus;
}
export interface KonvoCodexLogin {
	id: string;
	verification_url: string;
	user_code: string;
	expires_at: string;
}

export class KonvoClaudeConnectError extends Error {
	constructor(
		readonly code: string,
		message: string,
	) {
		super(message);
	}
}

export function deviceTimezone(): string | null {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
	} catch {
		return null;
	}
}

class KonvoConnectionsStore {
	/** null while the first status request is still in flight. */
	microsoftConnected: boolean | null = null;
	/** False when the Microsoft consent predates a permission Konvo now needs; the card offers Reconnect. */
	microsoftGrantCurrent: boolean | null = null;
	/** Teams meeting transcripts are a separate consent (they need tenant admin approval). */
	microsoftTranscripts = false;
	clickup: KonvoClickUpStatus | null = null;
	claude: KonvoClaudeStatus | null = null;
	codex: KonvoCodexStatus | null = null;
	assistantProvider: KonvoAssistantProvider = 'claude';
	assistantAutonomy: KonvoAssistantAutonomy = 'approve';
	assistantSettings: KonvoAssistantSettings | null = null;
	/** The proactive engine's triggers and today's budget; null until Settings asks for it. */
	engine: KonvoProactiveEngine | null = null;
	busy: 'microsoft' | 'clickup' | 'claude' | 'codex' | null = null;
	private microsoftCheckedAt = 0;

	constructor() {
		makeAutoObservable(this);
	}

	get fetched(): boolean {
		return this.microsoftConnected != null || this.clickup != null;
	}

	/** Claude is offered when the instance has the runtime and the user hasn't linked it yet. */
	get claudeOffered(): boolean {
		return (
			!this.codex?.connected &&
			this.claude?.available === true &&
			!this.claude.connected &&
			this.claude.onboarding_dismissed !== true
		);
	}

	async refreshAssistant(): Promise<void> {
		const response = await http.get<KonvoAssistantStatus>(Endpoints.KONVO_ASSISTANT_STATUS, RETRY_RATE_LIMITS);
		if (response.body)
			runInAction(() => {
				this.claude = response.body!.claude;
				this.codex = response.body!.codex;
				this.assistantProvider = response.body!.provider;
				this.assistantAutonomy = response.body!.autonomy ?? 'approve';
				if (response.body!.settings) this.setAssistantSettings(response.body!.settings);
				else this.assistantSettings = null;
			});
		// The assistant works in the user's timezone; until they have one, tell it this device's.
		const settings = response.body?.settings;
		const device = deviceTimezone();
		if (settings && settings.timezone == null && device) {
			void this.updateAssistantSettings({timezone: device}).catch(() => undefined);
		}
	}
	async updateAssistantSettings(patch: {streaming?: boolean; timezone?: string | null}): Promise<void> {
		const response = await http.patch<{settings: KonvoAssistantSettings}>(Endpoints.KONVO_ASSISTANT_SETTINGS, {
			body: patch,
		});
		runInAction(() => {
			if (response.body?.settings) this.setAssistantSettings(response.body.settings);
		});
	}
	/** The engine's settings, whichever request brought them in last. */
	get proactiveSettings(): KonvoProactiveSettings | null {
		return this.engine?.settings ?? this.assistantSettings?.proactive ?? null;
	}
	async refreshEngine(): Promise<void> {
		const response = await http.get<KonvoProactiveEngine>(Endpoints.KONVO_ASSISTANT_ENGINE);
		runInAction(() => {
			if (!response.body) return;
			this.engine = response.body;
			if (this.assistantSettings)
				this.assistantSettings = {...this.assistantSettings, proactive: response.body.settings};
		});
	}
	/** Applied optimistically so switches flip at once; rolled back if the server refuses. */
	async updateProactiveSettings(patch: KonvoProactiveSettingsPatch): Promise<void> {
		const previous = {engine: this.engine, assistantSettings: this.assistantSettings};
		const current = this.proactiveSettings;
		if (current) this.setProactiveSettings(applyProactivePatch(current, patch));
		try {
			const response = await http.patch<{settings: KonvoAssistantSettings}>(Endpoints.KONVO_ASSISTANT_SETTINGS, {
				body: {proactive: patch},
			});
			runInAction(() => {
				if (response.body?.settings) this.setAssistantSettings(response.body.settings);
			});
		} catch (error) {
			runInAction(() => {
				this.engine = previous.engine;
				this.assistantSettings = previous.assistantSettings;
			});
			throw error;
		}
	}
	/** Tells the engine whether something it surfaced helped. Best effort: never blocks the action itself. */
	reportProactiveOutcome(itemId: string, outcome: KonvoProactiveOutcome): void {
		void http
			.post(Endpoints.KONVO_ASSISTANT_ENGINE_OUTCOME(itemId), {body: {outcome}, mode: 'silent'})
			.catch(() => undefined);
	}
	/** The engine's record of surfaced items as a JSON document, byte for byte, for the eval harness. */
	async fetchProactiveFeedbackExport(outcome: 'bad' | 'all'): Promise<string> {
		const response = await http.get<string>(Endpoints.KONVO_ASSISTANT_ENGINE_FEEDBACK_EXPORT, {
			query: {outcome},
			parse: 'text',
		});
		return response.text ?? response.body;
	}
	private setAssistantSettings(settings: KonvoAssistantSettings): void {
		this.assistantSettings = settings;
		if (settings.proactive) this.setProactiveSettings(settings.proactive);
	}
	private setProactiveSettings(settings: KonvoProactiveSettings): void {
		if (this.assistantSettings) this.assistantSettings = {...this.assistantSettings, proactive: settings};
		if (this.engine)
			this.engine = {
				...this.engine,
				settings,
				triggers: this.engine.triggers.map((trigger) => ({
					...trigger,
					enabled: settings.triggers[trigger.kind] ?? trigger.enabled,
				})),
			};
	}
	/** Stops the reply the assistant is writing right now. */
	async cancelAssistantTurn(): Promise<void> {
		await http.post(Endpoints.KONVO_ASSISTANT_CANCEL, {body: {}});
	}
	async updateAssistantAutonomy(autonomy: KonvoAssistantAutonomy): Promise<void> {
		const response = await http.patch<{autonomy: KonvoAssistantAutonomy}>(Endpoints.KONVO_ASSISTANT_POLICY, {
			body: {autonomy},
		});
		runInAction(() => {
			this.assistantAutonomy = response.body?.autonomy ?? autonomy;
		});
	}
	async selectAssistant(provider: KonvoAssistantProvider): Promise<void> {
		await http.post(Endpoints.KONVO_ASSISTANT_PROVIDER, {body: {provider}});
		runInAction(() => {
			this.assistantProvider = provider;
		});
	}
	async startCodexLink(): Promise<KonvoCodexLogin> {
		try {
			const response = await http.post<KonvoCodexLogin>(Endpoints.KONVO_CODEX_LINK_START, {body: {}, timeoutMs: 45000});
			if (!response.body?.id || !response.body.user_code || !response.body.verification_url)
				throw new Error('Could not start Codex sign-in.');
			return response.body;
		} catch (error) {
			throw new Error(
				(error as {body?: {message?: string}})?.body?.message ?? 'Could not start Codex sign-in. Please try again.',
			);
		}
	}
	async pollCodexLink(
		id: string,
	): Promise<{state: 'pending' | 'expired' | 'failed' | 'connected'; status?: KonvoCodexStatus}> {
		const response = await http.post<{
			state: 'pending' | 'expired' | 'failed' | 'connected';
			status?: KonvoCodexStatus;
		}>(Endpoints.KONVO_CODEX_LINK_STATUS, {body: {id}, timeoutMs: 45000});
		if (!response.body) throw new Error('Could not check Codex sign-in.');
		if (response.body.state === 'connected' && response.body.status)
			runInAction(() => {
				this.codex = response.body!.status!;
				this.assistantProvider = 'codex';
			});
		return response.body;
	}
	cancelCodexLink(id: string): void {
		void http.post(Endpoints.KONVO_CODEX_LINK_CANCEL, {body: {id}, mode: 'silent'}).catch(() => undefined);
	}
	async disconnectCodex(): Promise<void> {
		runInAction(() => {
			this.busy = 'codex';
		});
		try {
			const response = await http.post<KonvoCodexStatus>(Endpoints.KONVO_CODEX_DISCONNECT, {body: {}});
			runInAction(() => {
				this.codex = response.body ?? null;
			});
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}
	async updateCodexSettings(patch: {enabled?: boolean; memory?: string}): Promise<void> {
		const response = await http.patch<KonvoCodexStatus>(Endpoints.KONVO_CODEX_SETTINGS, {body: patch});
		runInAction(() => {
			this.codex = response.body ?? this.codex;
		});
	}
	async resetCodexConversation(): Promise<void> {
		const response = await http.post<KonvoCodexStatus>(Endpoints.KONVO_CODEX_RESET, {body: {}});
		runInAction(() => {
			this.codex = response.body ?? this.codex;
		});
	}

	async refreshClaude(): Promise<void> {
		try {
			const response = await http.get<KonvoClaudeStatus>(Endpoints.KONVO_CLAUDE_STATUS, RETRY_RATE_LIMITS);
			runInAction(() => {
				this.claude = response.body ?? null;
			});
		} catch {
			runInAction(() => {
				this.claude = null;
			});
		}
	}

	/** Verifies + stores a `claude setup-token` value. Throws KonvoClaudeConnectError with a user-facing message. */
	async connectClaude(token: string): Promise<void> {
		runInAction(() => {
			this.busy = 'claude';
		});
		try {
			const response = await http.post<KonvoClaudeStatus & {error?: string; message?: string}>(
				Endpoints.KONVO_CLAUDE_CONNECT,
				{body: {token}},
			);
			runInAction(() => {
				this.claude = response.body ?? this.claude;
				if (this.claude?.connected) this.assistantProvider = 'claude';
			});
		} catch (error) {
			const body = (error as {body?: {error?: string; message?: string}} | undefined)?.body;
			throw new KonvoClaudeConnectError(
				body?.error ?? 'failed',
				body?.message ?? 'Could not connect Claude. Please try again.',
			);
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}

	/** Server-driven linking, step 1: the API creates a PKCE session and returns the sign-in link. */
	async startClaudeLink(): Promise<{id: string; authUrl: string}> {
		try {
			const response = await http.post<{id?: string; auth_url?: string; error?: string}>(
				Endpoints.KONVO_CLAUDE_LINK_START,
				{body: {}, timeoutMs: 10_000},
			);
			const body = response.body;
			if (!body?.id || !body.auth_url)
				throw new KonvoClaudeConnectError(body?.error ?? 'failed', 'Could not start Claude sign-in.');
			return {id: body.id, authUrl: body.auth_url};
		} catch (error) {
			if (error instanceof KonvoClaudeConnectError) throw error;
			const body = (error as {body?: {error?: string}} | undefined)?.body;
			throw new KonvoClaudeConnectError(
				body?.error ?? 'failed',
				body?.error === 'unavailable'
					? 'The assistant runtime is not installed on this Konvo server yet.'
					: 'Could not start Claude sign-in. Please try again.',
			);
		}
	}

	/** Server-driven linking, step 2: exchange the one-time code for a token. */
	async submitClaudeLinkCode(id: string, code: string): Promise<void> {
		runInAction(() => {
			this.busy = 'claude';
		});
		try {
			const response = await http.post<KonvoClaudeStatus & {error?: string; message?: string}>(
				Endpoints.KONVO_CLAUDE_LINK_CODE,
				{body: {id, code}, timeoutMs: 40_000},
			);
			runInAction(() => {
				this.claude = response.body ?? this.claude;
				if (this.claude?.connected) this.assistantProvider = 'claude';
			});
		} catch (error) {
			const body = (error as {body?: {error?: string; message?: string}} | undefined)?.body;
			throw new KonvoClaudeConnectError(
				body?.error ?? 'failed',
				body?.message ?? 'Could not connect Claude. Please try again.',
			);
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}

	/** Maybe later on the onboarding prompt: remembered server-side. */
	async dismissClaudeOnboarding(): Promise<void> {
		try {
			const response = await http.post<KonvoClaudeStatus>(Endpoints.KONVO_CLAUDE_ONBOARDING_DISMISS, {
				body: {},
				mode: 'silent',
			});
			runInAction(() => {
				this.claude = response.body ?? (this.claude ? {...this.claude, onboarding_dismissed: true} : this.claude);
			});
		} catch {
			runInAction(() => {
				if (this.claude) this.claude = {...this.claude, onboarding_dismissed: true};
			});
		}
	}

	cancelClaudeLink(): void {
		void http.post(Endpoints.KONVO_CLAUDE_LINK_CANCEL, {body: {}, mode: 'silent'}).catch(() => undefined);
	}

	async disconnectClaude(): Promise<void> {
		runInAction(() => {
			this.busy = 'claude';
		});
		try {
			const response = await http.post<KonvoClaudeStatus>(Endpoints.KONVO_CLAUDE_DISCONNECT, {body: {}});
			runInAction(() => {
				this.claude = response.body ?? this.claude;
			});
		} catch {
			await this.refreshClaude();
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}

	async updateClaudeSettings(patch: {enabled?: boolean; model?: KonvoClaudeModel; memory?: string}): Promise<void> {
		const response = await http.patch<KonvoClaudeStatus>(Endpoints.KONVO_CLAUDE_SETTINGS, {body: patch});
		runInAction(() => {
			this.claude = response.body ?? this.claude;
		});
	}

	async resetClaudeConversation(): Promise<void> {
		const response = await http.post<KonvoClaudeStatus>(Endpoints.KONVO_CLAUDE_RESET, {body: {}});
		runInAction(() => {
			this.claude = response.body ?? this.claude;
		});
	}

	/** Publish a status another request already learned (the events endpoint returns it). */
	setMicrosoftConnected(connected: boolean): void {
		this.microsoftConnected = connected;
	}

	/** Cheap read for background work: only hits the API when the cached value is stale. */
	async ensureMicrosoftStatus(maxAgeMs: number): Promise<boolean> {
		if (this.microsoftConnected != null && Date.now() - this.microsoftCheckedAt < maxAgeMs) {
			return this.microsoftConnected;
		}
		await this.refreshMicrosoft();
		return this.microsoftConnected === true;
	}

	async refresh(): Promise<void> {
		await Promise.all([
			this.refreshMicrosoft(),
			this.refreshClickUp(),
			this.refreshAssistant().catch(() => this.refreshClaude()),
		]);
	}

	async refreshMicrosoft(): Promise<void> {
		try {
			// A failure reads as "not connected" (prompts, no calendar): wait out a 429 instead.
			const response = await http.get<{connected: boolean; grant_current?: boolean; transcripts?: boolean}>(
				Endpoints.KONVO_MSGRAPH_STATUS,
				RETRY_RATE_LIMITS,
			);
			runInAction(() => {
				this.microsoftConnected = response.body?.connected ?? false;
				this.microsoftGrantCurrent = response.body?.grant_current !== false;
				this.microsoftTranscripts = response.body?.transcripts === true;
				this.microsoftCheckedAt = Date.now();
			});
		} catch {
			runInAction(() => {
				this.microsoftConnected = false;
				this.microsoftCheckedAt = Date.now();
			});
		}
	}

	async refreshClickUp(): Promise<void> {
		try {
			const response = await http.get<KonvoClickUpStatus>(Endpoints.KONVO_CLICKUP_OAUTH_STATUS, RETRY_RATE_LIMITS);
			runInAction(() => {
				this.clickup = response.body ?? null;
			});
		} catch {
			runInAction(() => {
				this.clickup = null;
			});
		}
	}

	/** `transcripts` also asks for Teams meeting transcripts on top of mail and calendar. */
	async connectMicrosoft(transcripts = false): Promise<void> {
		runInAction(() => {
			this.busy = 'microsoft';
		});
		try {
			const response = await http.post<{url: string}>(Endpoints.KONVO_MSGRAPH_CONNECT, {
				body: connectionSignInBody(transcripts ? {transcripts: true} : {}),
			});
			// The phone's in-app sheet has closed by the time this returns: show the result.
			if (response.body?.url && (await openConnectionSignIn(response.body.url)) !== 'opened')
				await this.refreshMicrosoft();
		} catch {
			// Nothing to recover: the card keeps showing "Not connected".
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}

	async disconnectMicrosoft(): Promise<void> {
		runInAction(() => {
			this.busy = 'microsoft';
		});
		try {
			await http.post(Endpoints.KONVO_MSGRAPH_DISCONNECT, {body: {}});
			runInAction(() => {
				this.microsoftConnected = false;
				this.microsoftCheckedAt = Date.now();
			});
			// Imported lazily: KonvoCalendar publishes its status back into this store, and a
			// static import both ways would be a cycle between two module singletons.
			void import('@app/features/konvo_calendar/state/KonvoCalendar').then((module) => module.default.refresh());
		} catch {
			await this.refreshMicrosoft();
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}

	async connectClickUp(): Promise<void> {
		runInAction(() => {
			this.busy = 'clickup';
		});
		try {
			const response = await http.post<{url: string}>(Endpoints.KONVO_CLICKUP_OAUTH_CONNECT, {
				body: connectionSignInBody({}),
			});
			if (response.body?.url && (await openConnectionSignIn(response.body.url)) !== 'opened')
				await this.refreshClickUp();
		} catch {
			// Same as above: status stays disconnected.
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}

	async disconnectClickUp(): Promise<void> {
		runInAction(() => {
			this.busy = 'clickup';
		});
		try {
			await http.post(Endpoints.KONVO_CLICKUP_OAUTH_DISCONNECT, {body: {}});
			runInAction(() => {
				this.clickup = this.clickup ? {...this.clickup, connected: false, clickup_email: null} : this.clickup;
			});
		} catch {
			await this.refreshClickUp();
		} finally {
			runInAction(() => {
				this.busy = null;
			});
		}
	}
}

export default new KonvoConnectionsStore();
